export const FONT_FAMILY_MAP: Record<string, string> = {
  sans: "ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif",
  serif: "ui-serif, Georgia, Cambria, 'Times New Roman', serif",
  mono: "ui-monospace, SFMono-Regular, Menlo, monospace",
  display: "Georgia, 'Times New Roman', serif",
};

export type CardTypographyPreset = {
  id: string;
  name: string;
  description: string;
  values: {
    tierNameFontFamily: string;
    tierNameFontWeight: string;
    tierNameFontStyle: string;
    tierNameTransform: string;
    priceFontFamily: string;
    priceFontWeight: string;
    priceFontStyle: string;
    featureFontFamily: string;
    featureFontWeight: string;
    featureFontStyle: string;
    buttonFontFamily: string;
    buttonFontWeight: string;
    buttonTransform: string;
  };
};

export const CARD_TYPOGRAPHY_PRESETS: CardTypographyPreset[] = [
  {
    id: "modern",
    name: "Modern",
    description: "Clean sans-serif, balanced weights",
    values: {
      tierNameFontFamily: "sans", tierNameFontWeight: "600", tierNameFontStyle: "normal", tierNameTransform: "none",
      priceFontFamily: "sans", priceFontWeight: "700", priceFontStyle: "normal",
      featureFontFamily: "sans", featureFontWeight: "400", featureFontStyle: "normal",
      buttonFontFamily: "sans", buttonFontWeight: "500", buttonTransform: "none",
    },
  },
  {
    id: "classic",
    name: "Classic",
    description: "Serif elegance, timeless feel",
    values: {
      tierNameFontFamily: "serif", tierNameFontWeight: "600", tierNameFontStyle: "normal", tierNameTransform: "none",
      priceFontFamily: "serif", priceFontWeight: "700", priceFontStyle: "normal",
      featureFontFamily: "serif", featureFontWeight: "400", featureFontStyle: "normal",
      buttonFontFamily: "sans", buttonFontWeight: "500", buttonTransform: "none",
    },
  },
  {
    id: "bold",
    name: "Bold",
    description: "Heavy weights, uppercase accents",
    values: {
      tierNameFontFamily: "sans", tierNameFontWeight: "800", tierNameFontStyle: "normal", tierNameTransform: "uppercase",
      priceFontFamily: "sans", priceFontWeight: "800", priceFontStyle: "normal",
      featureFontFamily: "sans", featureFontWeight: "500", featureFontStyle: "normal",
      buttonFontFamily: "sans", buttonFontWeight: "700", buttonTransform: "uppercase",
    },
  },
  {
    id: "editorial",
    name: "Editorial",
    description: "Display serif headings, italic flair",
    values: {
      tierNameFontFamily: "display", tierNameFontWeight: "700", tierNameFontStyle: "italic", tierNameTransform: "none",
      priceFontFamily: "display", priceFontWeight: "700", priceFontStyle: "normal",
      featureFontFamily: "serif", featureFontWeight: "400", featureFontStyle: "italic",
      buttonFontFamily: "serif", buttonFontWeight: "600", buttonTransform: "none",
    },
  },
];

export const DEFAULT_CARD_TYPOGRAPHY = CARD_TYPOGRAPHY_PRESETS[0];

export type PricingCardTypoFields = {
  tierNameFontFamily?: string;
  tierNameFontWeight?: string;
  tierNameFontStyle?: string;
  tierNameTransform?: string;
  priceFontFamily?: string;
  priceFontWeight?: string;
  priceFontStyle?: string;
  featureFontFamily?: string;
  featureFontWeight?: string;
  featureFontStyle?: string;
  buttonFontFamily?: string;
  buttonFontWeight?: string;
  buttonTransform?: string;
};

// ── Thank You typography ────────────────────────────────────────────────

export type ThankYouPreset = {
  id: string;
  name: string;
  description: string;
  values: {
    headingFontFamily: string;
    headingFontWeight: string;
    headingFontStyle: string;
    headingTransform: string;
    messageFontFamily: string;
    messageFontWeight: string;
    messageFontStyle: string;
    noteFontFamily: string;
    noteFontWeight: string;
    noteFontStyle: string;
    buttonFontFamily: string;
    buttonFontWeight: string;
    buttonTransform: string;
  };
};

export const THANK_YOU_PRESETS: ThankYouPreset[] = [
  {
    id: "modern",
    name: "Modern",
    description: "Clean sans-serif",
    values: {
      headingFontFamily: "sans", headingFontWeight: "700", headingFontStyle: "normal", headingTransform: "none",
      messageFontFamily: "sans", messageFontWeight: "400", messageFontStyle: "normal",
      noteFontFamily: "sans", noteFontWeight: "400", noteFontStyle: "normal",
      buttonFontFamily: "sans", buttonFontWeight: "500", buttonTransform: "none",
    },
  },
  {
    id: "classic",
    name: "Classic",
    description: "Serif elegance",
    values: {
      headingFontFamily: "serif", headingFontWeight: "700", headingFontStyle: "normal", headingTransform: "none",
      messageFontFamily: "serif", messageFontWeight: "400", messageFontStyle: "normal",
      noteFontFamily: "serif", noteFontWeight: "400", noteFontStyle: "italic",
      buttonFontFamily: "sans", buttonFontWeight: "500", buttonTransform: "none",
    },
  },
  {
    id: "bold",
    name: "Bold",
    description: "Heavy with uppercase",
    values: {
      headingFontFamily: "sans", headingFontWeight: "800", headingFontStyle: "normal", headingTransform: "uppercase",
      messageFontFamily: "sans", messageFontWeight: "500", messageFontStyle: "normal",
      noteFontFamily: "sans", noteFontWeight: "500", noteFontStyle: "normal",
      buttonFontFamily: "sans", buttonFontWeight: "700", buttonTransform: "uppercase",
    },
  },
  {
    id: "editorial",
    name: "Editorial",
    description: "Display serif heading",
    values: {
      headingFontFamily: "display", headingFontWeight: "700", headingFontStyle: "italic", headingTransform: "none",
      messageFontFamily: "serif", messageFontWeight: "400", messageFontStyle: "normal",
      noteFontFamily: "serif", noteFontWeight: "400", noteFontStyle: "italic",
      buttonFontFamily: "serif", buttonFontWeight: "600", buttonTransform: "none",
    },
  },
];

export const DEFAULT_THANK_YOU_PRESET = THANK_YOU_PRESETS[0];

export type ThankYouTypoFields = {
  headingFontFamily?: string;
  headingFontWeight?: string;
  headingFontStyle?: string;
  headingTransform?: string;
  headingFontSize?: string;
  headingColor?: string;
  messageFontFamily?: string;
  messageFontWeight?: string;
  messageFontStyle?: string;
  messageColor?: string;
  noteFontFamily?: string;
  noteFontWeight?: string;
  noteFontStyle?: string;
  noteColor?: string;
  buttonFontFamily?: string;
  buttonFontWeight?: string;
  buttonTransform?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  buttonRadius?: string;
};

const HEADING_SIZE_MAP: Record<string, string> = {
  sm: "1.25rem",
  base: "1.5rem",
  lg: "1.875rem",
  xl: "2.25rem",
  "2xl": "3rem",
};

const RADIUS_MAP: Record<string, string> = {
  none: "0",
  sm: "0.25rem",
  md: "0.375rem",
  lg: "0.75rem",
  full: "9999px",
};

export function resolveThankYouStyles(d?: ThankYouTypoFields) {
  const v = { ...DEFAULT_THANK_YOU_PRESET.values, ...(d || {}) };
  const fam = (k: string) => FONT_FAMILY_MAP[k] || FONT_FAMILY_MAP.sans;
  return {
    heading: {
      fontFamily: fam(v.headingFontFamily),
      fontSize: HEADING_SIZE_MAP[d?.headingFontSize || "base"] || HEADING_SIZE_MAP.base,
      fontWeight: v.headingFontWeight as any,
      fontStyle: v.headingFontStyle,
      textTransform: v.headingTransform as any,
      letterSpacing: v.headingFontFamily === "display" ? "-0.02em" : undefined,
      color: d?.headingColor || "#111827",
    } as React.CSSProperties,
    message: {
      fontFamily: fam(v.messageFontFamily),
      fontWeight: v.messageFontWeight as any,
      fontStyle: v.messageFontStyle,
      color: d?.messageColor || "#6b7280",
    } as React.CSSProperties,
    note: {
      fontFamily: fam(v.noteFontFamily),
      fontWeight: v.noteFontWeight as any,
      fontStyle: v.noteFontStyle,
      color: d?.noteColor || "#6b7280",
    } as React.CSSProperties,
    button: {
      fontFamily: fam(v.buttonFontFamily),
      fontWeight: v.buttonFontWeight as any,
      textTransform: v.buttonTransform as any,
      backgroundColor: d?.buttonBgColor || "#111827",
      color: d?.buttonTextColor || "#ffffff",
      borderRadius: RADIUS_MAP[d?.buttonRadius || "lg"] || RADIUS_MAP.lg,
    } as React.CSSProperties,
  };
}

export function resolveCardTypographyStyles(d?: PricingCardTypoFields) {
  const v = { ...DEFAULT_CARD_TYPOGRAPHY.values, ...(d || {}) };
  const fam = (k: string) => FONT_FAMILY_MAP[k] || FONT_FAMILY_MAP.sans;
  return {
    tierName: {
      fontFamily: fam(v.tierNameFontFamily),
      fontWeight: v.tierNameFontWeight as any,
      fontStyle: v.tierNameFontStyle,
      textTransform: v.tierNameTransform as any,
      letterSpacing: v.tierNameFontFamily === "display" ? "-0.01em" : undefined,
    } as React.CSSProperties,
    price: {
      fontFamily: fam(v.priceFontFamily),
      fontWeight: v.priceFontWeight as any,
      fontStyle: v.priceFontStyle,
      letterSpacing: v.priceFontFamily === "display" ? "-0.02em" : undefined,
    } as React.CSSProperties,
    feature: {
      fontFamily: fam(v.featureFontFamily),
      fontWeight: v.featureFontWeight as any,
      fontStyle: v.featureFontStyle,
    } as React.CSSProperties,
    button: {
      fontFamily: fam(v.buttonFontFamily),
      fontWeight: v.buttonFontWeight as any,
      textTransform: v.buttonTransform as any,
    } as React.CSSProperties,
  };
}
